import React, { useEffect, useState } from 'react';
import Input from '../../../shared/components/Input/Input';
import Dialog from '../../../shared/components/Dialog/Dialog';
import { uploadRHReferenceImage } from '../rh.api';

const empty = {
  projectName: '', client: '', buildingType: '', sector: '', year: '', location: '',
  surface: '', worksAmount: '', role: '', mission: '', workPackages: '', deliverables: '',
  imagePath: '', confidentiality: 'internal', showInCv: false, showInPremiumSheet: true,
};

export default function RHReferenceDialog({ open, item, loading, error, onClose, onSubmit }) {
  const [form, setForm] = useState(empty);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      ...empty,
      ...item,
      workPackages: Array.isArray(item?.workPackages) ? item.workPackages.join(', ') : item?.workPackages || '',
      deliverables: Array.isArray(item?.deliverables) ? item.deliverables.join(', ') : item?.deliverables || '',
    });
    setImageFile(null);
    setImagePreview(item?.imagePath || '');
    setUploadError('');
  }, [item, open]);

  useEffect(() => {
    if (!imageFile) return undefined;
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const change = ({ target }) => setForm((value) => ({
    ...value,
    [target.name]: target.type === 'checkbox' ? target.checked : target.value,
  }));
  const list = (value) => value.split(',').map((entry) => entry.trim()).filter(Boolean);

  const selectImage = ({ target }) => {
    const file = target.files?.[0] || null;
    setUploadError('');
    if (!file) return setImageFile(null);
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      target.value = '';
      return setUploadError('Sélectionnez une image JPEG, PNG ou WebP.');
    }
    if (file.size > 5 * 1024 * 1024) {
      target.value = '';
      return setUploadError('L’image ne doit pas dépasser 5 Mo.');
    }
    return setImageFile(file);
  };

  const submit = async () => {
    setUploadError('');
    setUploading(true);
    try {
      let imagePath = form.imagePath;
      if (imageFile) {
        const uploaded = await uploadRHReferenceImage(imageFile);
        imagePath = uploaded.imagePath;
      }
      await onSubmit({
        ...form,
        imagePath,
        workPackages: list(form.workPackages),
        deliverables: list(form.deliverables),
      });
    } catch (uploadFailure) {
      setUploadError(uploadFailure.message || 'Le chargement de l’image a échoué.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      title={item ? 'Modifier la référence' : 'Ajouter une référence'}
      submitLabel="Enregistrer"
      loading={loading || uploading}
      size="large"
    >
      {(error || uploadError) && <p className="rh-form-error">{error || uploadError}</p>}
      <div className="rh-dialog-grid">
        <Input required fullWidth label="Nom du projet" name="projectName" value={form.projectName} onChange={change} />
        <Input fullWidth label="Client" name="client" value={form.client || ''} onChange={change} />
        <Input fullWidth label="Type de bâtiment" name="buildingType" value={form.buildingType || ''} onChange={change} />
        <Input fullWidth label="Secteur" name="sector" value={form.sector || ''} onChange={change} />
        <Input fullWidth type="number" label="Année" name="year" value={form.year || ''} onChange={change} />
        <Input fullWidth label="Lieu" name="location" value={form.location || ''} onChange={change} />
        <Input fullWidth type="number" label="Surface" name="surface" value={form.surface || ''} onChange={change} />
        <Input fullWidth type="number" label="Montant des travaux" name="worksAmount" value={form.worksAmount || ''} onChange={change} />
        <Input fullWidth label="Rôle du collaborateur" name="role" value={form.role || ''} onChange={change} />
        <Input fullWidth multiline rows={4} label="Mission réalisée" name="mission" value={form.mission || ''} onChange={change} />
        <Input fullWidth label="Lots étudiés" name="workPackages" value={form.workPackages} onChange={change} />
        <Input fullWidth label="Livrables" name="deliverables" value={form.deliverables} onChange={change} />
        <label className="custom-input custom-input--full rh-reference-image-field">
          <span className="custom-input__label">Image de la réalisation</span>
          <input className="custom-input__field" type="file" accept="image/jpeg,image/png,image/webp" onChange={selectImage} />
          <small>JPEG, PNG ou WebP · 5 Mo maximum</small>
          {imagePreview && <img src={imagePreview} alt="Aperçu de la réalisation" />}
        </label>
        <label className="custom-input custom-input--full">
          <span className="custom-input__label">Confidentialité</span>
          <select className="custom-input__field" name="confidentiality" value={form.confidentiality} onChange={change}>
            <option value="internal">Interne</option>
            <option value="confidential">Confidentiel</option>
            <option value="public">Publiable</option>
          </select>
        </label>
        <label className="rh-check-field">
          <input type="checkbox" name="showInPremiumSheet" checked={form.showInPremiumSheet} onChange={change} />
          Afficher dans la fiche premium
        </label>
      </div>
    </Dialog>
  );
}
